import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Users, Copy, Check, Gift, Pencil, CheckCircle, Clock, ChevronLeft, ChevronRight, Briefcase, XCircle } from 'lucide-react';
import { Input } from './ui/input';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { toast } from 'sonner';
import { Skeleton } from './ui/skeleton';

const ReferralSection = ({ profile }) => {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [editingCode, setEditingCode] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [savingCode, setSavingCode] = useState(false);
  const [page, setPage] = useState(1);
  const [gcashNumber, setGcashNumber] = useState('');
  const [editingGcash, setEditingGcash] = useState(false);
  const [savingGcash, setSavingGcash] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, [profile?.id, page]);

  const fetchReferralData = async () => {
    if (!profile) return;

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get(`/api/referrals/my-code?page=${page}&per_page=10`, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      setReferralData(response.data);
      setCustomCode(response.data?.code || '');
      setGcashNumber(response.data?.ambassador?.gcash_number || '');
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = async () => {
    const referralLink = `${window.location.origin}/auth?ref=${referralData.code}`;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy. Try long-pressing the link to copy.');
    }
  };

  const saveCustomCode = async () => {
    const code = customCode.trim().toUpperCase();
    if (code.length < 4 || code.length > 20) {
      toast.error('Code must be 4-20 characters');
      return;
    }
    setSavingCode(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) { toast.error('Session expired. Please log in again.'); return; }
      await api.put('/api/referrals/custom-code', { code }, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setReferralData(prev => ({ ...prev, code }));
      setEditingCode(false);
      toast.success('Referral code updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update code');
    } finally {
      setSavingCode(false);
    }
  };

  const saveGcash = async () => {
    setSavingGcash(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) { toast.error('Session expired. Please log in again.'); return; }
      const res = await api.put('/api/referrals/gcash', { gcash_number: gcashNumber }, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setReferralData(prev => ({
        ...prev,
        ambassador: { ...prev.ambassador, gcash_number: res.data.gcash_number }
      }));
      setEditingGcash(false);
      toast.success('GCash number saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save GCash number');
    } finally {
      setSavingGcash(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Referral Program</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32" />
        </CardContent>
      </Card>
    );
  }

  const isAmbassador = referralData?.ambassador?.is_referrer;
  const pagination = referralData?.pagination;

  // Ambassador view — cash payout tracking
  if (isAmbassador) {
    const amb = referralData.ambassador;
    return (
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="text-amber-600" size={24} />
            Ambassador Program
            <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 ml-2">Ambassador</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Payout Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">P{(amb.pending_amount || 0).toFixed(0)}</p>
              <p className="text-xs text-muted-foreground">Pending Payout</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-600">P{(amb.paid_amount || 0).toFixed(0)}</p>
              <p className="text-xs text-muted-foreground">Total Earned</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-primary">{referralData?.referrals_count || 0}</p>
              <p className="text-xs text-muted-foreground">Total Referrals</p>
            </div>
          </div>

          {/* How it works */}
          <div className="bg-white/70 rounded-lg p-3 text-sm text-muted-foreground space-y-1">
            <p className="font-semibold text-foreground mb-1">How it works:</p>
            <p><strong>Job Seekers:</strong> P20 (profile complete) + P30 (ID verified) = <strong>P50</strong></p>
            <p><strong>Employers:</strong> P300 (ID verified) + P100 (1st job posted) = <strong>P400</strong></p>
            <p className="text-xs">Bonuses: P200 for employers with 10+ employees. P200 if you onboard 5 employers in a month.</p>
          </div>

          {/* Custom Code Editor */}
          <div className="bg-white rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold">Your Referral Code</p>
              {!editingCode && (
                <Button variant="ghost" size="sm" onClick={() => setEditingCode(true)}>
                  <Pencil size={14} className="mr-1" /> Customize
                </Button>
              )}
            </div>
            {editingCode ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  value={customCode}
                  onChange={(e) => setCustomCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase())}
                  maxLength={20}
                  placeholder="e.g., MARIA2026"
                  className="font-mono"
                />
                <Button size="sm" onClick={saveCustomCode} disabled={savingCode}>
                  {savingCode ? 'Saving...' : 'Save'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditingCode(false); setCustomCode(referralData.code); }}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <code className="bg-muted px-3 py-1.5 rounded text-lg font-bold">{referralData?.code}</code>
                <Button onClick={copyReferralLink} size="sm" variant="outline">
                  {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                  Copy Link
                </Button>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1 break-all">
              Share: www.boholtrabaho.com/auth?ref={referralData?.code}
            </p>
          </div>

          {/* GCash Number */}
          <div className="bg-white rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold">GCash Number</p>
              {!editingGcash && (
                <Button variant="ghost" size="sm" onClick={() => setEditingGcash(true)}>
                  <Pencil size={14} className="mr-1" /> {amb.gcash_number ? 'Edit' : 'Add'}
                </Button>
              )}
            </div>
            {editingGcash ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  value={gcashNumber}
                  onChange={(e) => setGcashNumber(e.target.value.replace(/[^0-9+]/g, ''))}
                  maxLength={13}
                  placeholder="09171234567"
                  className="font-mono"
                />
                <Button size="sm" onClick={saveGcash} disabled={savingGcash}>
                  {savingGcash ? 'Saving...' : 'Save'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditingGcash(false); setGcashNumber(amb.gcash_number || ''); }}>
                  Cancel
                </Button>
              </div>
            ) : (
              <p className="text-sm font-mono">
                {amb.gcash_number || <span className="text-muted-foreground italic">Not set — add your GCash number to receive payouts</span>}
              </p>
            )}
          </div>

          {/* Referred Users List */}
          {amb.referred_details && amb.referred_details.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2">Your Referrals</p>
              <div className="space-y-2">
                {amb.referred_details.map((ref) => {
                  const isEmployer = ref.role === 'employer' || ref.role === 'owner';
                  return (
                    <div key={ref.id} className={`bg-white rounded-lg p-2.5 ${ref.reversed ? 'border border-red-200 bg-red-50/50' : ''}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium flex items-center gap-1">
                            {ref.name}
                            {isEmployer && <Briefcase size={12} className="text-blue-500" />}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(ref.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      {ref.reversed ? (
                        <div className="mt-1.5">
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 text-xs">
                            <XCircle size={12} className="mr-1" /> Reversed — failed verification
                          </Badge>
                          <p className="text-xs text-red-600/70 mt-1">Payouts for this referral have been reversed. This business could not be verified as legitimate.</p>
                        </div>
                      ) : (
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        {isEmployer ? (
                          <>
                            {ref.employer_verified_payout ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">
                                <CheckCircle size={12} className="mr-1" /> Verified P300
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground text-xs">
                                <Clock size={12} className="mr-1" /> Verified
                              </Badge>
                            )}
                            {ref.employer_first_job_payout ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">
                                <CheckCircle size={12} className="mr-1" /> 1st Job P100
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground text-xs">
                                <Clock size={12} className="mr-1" /> 1st Job
                              </Badge>
                            )}
                            {ref.employer_10plus_payout && (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">
                                <CheckCircle size={12} className="mr-1" /> 10+ Emp P200
                              </Badge>
                            )}
                          </>
                        ) : (
                          <>
                            {ref.profile_complete_payout ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">
                                <CheckCircle size={12} className="mr-1" /> Profile P20
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground text-xs">
                                <Clock size={12} className="mr-1" /> Profile
                              </Badge>
                            )}
                            {ref.verification_payout ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">
                                <CheckCircle size={12} className="mr-1" /> ID P30
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground text-xs">
                                <Clock size={12} className="mr-1" /> ID
                              </Badge>
                            )}
                          </>
                        )}
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pagination — outside referral list so it's always visible */}
          {pagination && pagination.total_pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
              </p>
              <div className="flex gap-1">
                <Button
                  size="sm" variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft size={14} />
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= pagination.total_pages}
                >
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Standard view — credit-based referrals (for employers and non-ambassador seekers)
  return (
    <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gift className="text-primary" size={24} />
          Referral Program
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h4 className="font-semibold mb-2">Earn Credits by Referring</h4>
          <p className="text-sm text-muted-foreground mb-3">
            Share your unique referral link with other employers and earn <span className="font-bold text-primary">50 credits</span> for each verified employer who signs up using your link.
          </p>

          <div className="flex items-center justify-between gap-4 flex-wrap bg-white dark:bg-muted p-4 rounded-lg">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Users className="text-primary" size={24} />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold">Your Referrals</h3>
                <p className="text-sm text-muted-foreground">
                  {referralData?.referrals_count || 0} referrals · <span className="font-bold text-primary">{referralData?.credits_earned || 0} credits earned</span>
                </p>
              </div>
            </div>

            <Button
              onClick={copyReferralLink}
              size="sm"
              data-testid="copy-link-btn"
            >
              {copied ? <Check size={16} className="mr-1" /> : <Copy size={16} className="mr-1" />}
              <span className="hidden sm:inline">Copy Link</span>
              <span className="sm:hidden">Copy</span>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReferralSection;
